<script setup>

    import { computed, ref, watch } from 'vue'
    import { util } from '@/Helpers'
    import moment from 'moment'
    import { useElementHover } from '@vueuse/core'
    import { storeToRefs } from 'pinia'
    import { useCaregiverAvailabilityStore, useCaregiverSingleStore } from '@/Stores'
    import LatteSchedule from '@/Components/LatteSchedule.vue'
    import ClientCaregiverViewBlock from '@/Components/Caregiver/ClientCaregiverViewBlock.vue'
    import LiveInViewBlock from '@/Components/Caregiver/LiveInViewBlock.vue'
    import LiveOutViewBlock from '@/Components/Caregiver/LiveOutViewBlock.vue'
    import SearchViewBlock from '@/Components/Caregiver/SearchViewBlock.vue'
    import LwxSwitch from '../Forms/LwxSwitch.vue'

    const caregiverAvailabilityStore = useCaregiverAvailabilityStore()
    const caregiverSingleStore = useCaregiverSingleStore()
    const { userFocusedOn } = storeToRefs(caregiverSingleStore)

    const props = defineProps({
        caregiver: {
            type: Object,
            required: true,
        },
        searchSchedule: {
            type: Array,
            default: () => [],
        },
        searchLivingPreference: {
            type: String,
            required: true,
            validator: (value) => ['live_in', 'live_out'].includes(value)
        }
    })

    const verboseMode = ref(false)
    const showAssignedShifts = ref(false)

    const searchDisplayMode = computed(() => verboseMode.value ? 'search_verbose' : 'search')

    const liveInSwatch = ref(null)
    const liveOutSwatch = ref(null)
    const clientShiftSwatch = ref(null)

    const liveInSwatchHover = useElementHover(liveInSwatch, { delayEnter: 60, delayLeave: 60 })
    const liveOutSwatchHover = useElementHover(liveOutSwatch, { delayEnter: 60, delayLeave: 60 })
    const clientShiftSwatchHover = useElementHover(clientShiftSwatch, { delayEnter: 60, delayLeave: 60 })

    watch([liveInSwatchHover, liveOutSwatchHover, clientShiftSwatchHover ], () => {
        if (liveInSwatchHover.value) {
            userFocusedOn.value = 'live-in'
        } else if (liveOutSwatchHover.value) {
            userFocusedOn.value = 'live-out'
        } else if (clientShiftSwatchHover.value) {
            userFocusedOn.value = 'client_shift'
        } else {
            userFocusedOn.value = null
        }
    })

    const scheduleStartDate = ref(util.date.lastSunday())
    const pastScheduleStartDate = computed(() => {
        return moment(scheduleStartDate.value, util.date.date_format)
            .subtract(1, 'week')
            .format(util.date.date_format)
    })
    const scheduleState = ref(caregiverAvailabilityStore.computeScheduleState([]))
    let liveInShifts
    let liveOutGroupOneShifts
    let liveOutGroupTwoShifts
    let clientShifts
    let lastPeriodShifts
    let searchShifts
    let scheduleDayTimeDict
    let liveInBlocks
    let liveOutGroupOneBlocks
    let liveOutGroupTwoBlocks
    let clientShiftBlocks
    let lastPeriodShiftBlocks
    let searchBlocks

    function generateShiftBlocks() {
        scheduleDayTimeDict = computed(() => {
            let dict = {
                sunday: {
                    live_in_0: [],
                    live_out_0: [],
                    live_out_1: [],
                    search: []
                },
                monday: {
                    live_in_0: [],
                    live_out_0: [],
                    live_out_1: [],
                    search: []
                },
                tuesday: {
                    live_in_0: [],
                    live_out_0: [],
                    live_out_1: [],
                    search: []
                },
                wednesday: {
                    live_in_0: [],
                    live_out_0: [],
                    live_out_1: [],
                    search: []
                },
                thursday: {
                    live_in_0: [],
                    live_out_0: [],
                    live_out_1: [],
                    search: []
                },
                friday: {
                    live_in_0: [],
                    live_out_0: [],
                    live_out_1: [],
                    search: []
                },
                saturday: {
                    live_in_0: [],
                    live_out_0: [],
                    live_out_1: [],
                    search: []
                },
            }

            function buildDict(key, dict, shift)
            {
                dict[shift.day_of_week][key].push({
                    start_time:  shift.start_time,
                    duration: util.schedule.is2DaysTime(shift.start_time, shift.duration) ? util.date.getDayRemainMinute(shift.start_time) : shift.duration
                })

                if (util.schedule.is2DaysTime(shift.start_time, shift.duration)) {
                    const nextDay = util.date.nextDayOfWeek(shift.day_of_week)
                    dict[nextDay][key].push({
                        start_time: '00:00:00',
                        duration: shift.duration - util.date.getDayRemainMinute(shift.start_time)
                    })
                }

                return dict;
            }

            liveInShifts.value.forEach(shift => {
                dict = buildDict('live_in_0', dict, shift)
            })

            liveOutGroupOneShifts.value.forEach(shift => {
                dict = buildDict('live_out_0', dict, shift)
            })

            liveOutGroupTwoShifts.value.forEach(shift => {
                dict = buildDict('live_out_1', dict, shift)
            })

            searchShifts.value.forEach(shift => {
                dict = buildDict('search', dict, shift)
            })

            return dict;
        })

        liveInBlocks = computed(() => {
            if (liveInShifts) {}

            let data = []

            liveInShifts.value.forEach(shift => {

                let blocks = util.schedule.shiftToBlock(shift, shift, scheduleStartDate.value)
                blocks.forEach(block => {
                    scheduleDayTimeDict.value[block.day_of_week]['live_out_0'].forEach(info => {
                        if (util.schedule.isTimeOverlap(block.start_time, block.duration, info.start_time, info.duration)) {
                            block.overlaps = true
                        }
                    })
                    scheduleDayTimeDict.value[block.day_of_week]['live_out_1'].forEach(info => {
                        if (util.schedule.isTimeOverlap(block.start_time, block.duration, info.start_time, info.duration)) {
                            block.overlaps = true
                        }
                    })
                })

                data = data.concat(blocks)
            })

            return data;
        })

        liveOutGroupOneBlocks = computed(() => {
            if (liveOutGroupOneShifts) {}

            let data = []
            liveOutGroupOneShifts.value.forEach(shift => {
                let blocks = util.schedule.shiftToBlock(shift, shift, scheduleStartDate.value)
                blocks.forEach(block => {
                    block.group = 'a'
                    scheduleDayTimeDict.value[block.day_of_week]['live_in_0'].forEach(info => {
                        if (util.schedule.isTimeOverlap(block.start_time, block.duration, info.start_time, info.duration)) {
                            block.overlaps = true
                        }
                    })
                    scheduleDayTimeDict.value[block.day_of_week]['live_out_1'].forEach(info => {
                        if (util.schedule.isTimeOverlap(block.start_time, block.duration, info.start_time, info.duration)) {
                            block.overlaps = true
                        }
                    })
                })
                data = data.concat(blocks)
            })

            return data;
        })

        liveOutGroupTwoBlocks = computed(() => {
            if (liveOutGroupTwoShifts) {}

            let data = []
            liveOutGroupTwoShifts.value.forEach(shift => {
                let blocks = util.schedule.shiftToBlock(shift, shift, scheduleStartDate.value)
                blocks.forEach(block => {
                    block.group = 'b'
                    scheduleDayTimeDict.value[block.day_of_week]['live_in_0'].forEach(info => {
                        if (util.schedule.isTimeOverlap(block.start_time, block.duration, info.start_time, info.duration)) {
                            block.overlaps = true
                        }
                    })
                    scheduleDayTimeDict.value[block.day_of_week]['live_out_0'].forEach(info => {
                        if (util.schedule.isTimeOverlap(block.start_time, block.duration, info.start_time, info.duration)) {
                            block.overlaps = true
                        }
                    })
                })
                data = data.concat(blocks)
            })

            return data;
        })

        clientShiftBlocks = computed(() => {
            let data = []
            clientShifts.value.forEach(shift => {
                data = data.concat(util.schedule.shiftToBlock(shift, shift, scheduleStartDate.value))
            })
            return data;
        })

        lastPeriodShiftBlocks = computed(() => {
            if (!lastPeriodShifts) return [];

            let data = []
            lastPeriodShifts.value.forEach(shift => {
                data = data.concat(util.schedule.shiftToBlock(shift, shift, pastScheduleStartDate.value))
            })
            return data;
        })

        searchBlocks = computed(() => {
            if (searchShifts) {}

            let data = []
            searchShifts.value.forEach(shift => {
                let blocks = util.schedule.shiftToBlock(shift, shift, scheduleStartDate.value)
                blocks = blocks.filter(block => {
                    if (shift.day_of_week === 'saturday' && block.day_of_week === 'sunday' && block.continuation) {
                        return false;
                    }
                    return true;
                })
                data = data.concat(blocks)
            })

            return data;
        })
    }

    function scheduleStateToShift(stay_type, pos = 0) {
        const stayTypeData = scheduleState.value[stay_type][pos]
        const dayOfWeek = {}
        stayTypeData['day_of_week'].forEach(day => dayOfWeek[day] = true)

        const dayOfWeekWithData = Object.keys(dayOfWeek).filter(day => dayOfWeek[day])

        return dayOfWeekWithData.map(day => ({
            start_time: stayTypeData['start_time'],
            duration: stayTypeData['duration'],
            start_date: day != 'saturday' ? util.date.nextDayOfWeekDate(scheduleStartDate.value, day) : util.date.previousDate(scheduleStartDate.value),
            day_of_week: day,
            stay_type: stay_type
        }));
    }

    watch(() => props.caregiver, (value) => {
        if (value == null) {
            return;
        }

        scheduleState.value = caregiverAvailabilityStore.computeScheduleState(value.availabilities)
        liveInShifts = computed(() => {
            if (scheduleState.value == null) {
                return [];
            }

            if (scheduleStartDate.value < util.date.lastSunday()) {
                return [];
            }

            return scheduleStateToShift('live_in')
        })

        liveOutGroupOneShifts = computed(() => {
            if (scheduleState.value == null) {
                return [];
            }

            if (scheduleStartDate.value < util.date.lastSunday()) {
                return [];
            }

            return scheduleStateToShift('live_out', 0)
        })

        liveOutGroupTwoShifts = computed(() => {
            if (scheduleState.value == null) {
                return [];
            }

            if (scheduleStartDate.value < util.date.lastSunday()) {
                return [];
            }

            return scheduleStateToShift('live_out', 1)
        })

        clientShifts = computed(() => {
            if (!value.client_shifts) {
                return [];
            }

            let data = []
            value.client_shifts.forEach(clientShiftData => {
                data.push(util.schedule.toClientScheduleSupportShift(clientShiftData))
            })
            return data;
        })

        lastPeriodShifts = computed(() => {
            if (!value.last_period_shifts || !value.last_period_shifts.length) {
                return [];
            }

            return value.last_period_shifts.map(shift => ({
                start_time: shift.start_time,
                duration: shift.duration,
                day_of_week: shift.day_of_week,
                start_date: shift.day_of_week !== 'saturday'
                    ? util.date.nextDayOfWeekDate(pastScheduleStartDate.value, shift.day_of_week)
                    : util.date.previousDate(pastScheduleStartDate.value),
                client: shift.client
            }));
        })

        searchShifts = computed(() => {
            if (scheduleState.value == null) {
                return [];
            }

            if (scheduleStartDate.value < util.date.lastSunday()) {
                return [];
            }

            let data = []
            props.searchSchedule.forEach(availability => {
                const dayOfWeek = {}
                dayOfWeek[availability['day']] = true

                const dayOfWeekWithData = Object.keys(dayOfWeek).filter(day => dayOfWeek[day])

                data = data.concat(dayOfWeekWithData.map(day => ({
                    start_time: props.searchLivingPreference == 'live_out' ? util.date.formatTo24HourTime(availability['start_hour']) : '08:00:00',
                    duration: props.searchLivingPreference == 'live_out'
                        ? (availability['end_hour'] <= availability['start_hour']
                            ? (availability['end_hour'] + 24 - availability['start_hour'])
                            : (availability['end_hour'] - availability['start_hour'])) * 60
                        : 24 * 60,
                    start_date: day != 'saturday' ? util.date.nextDayOfWeekDate(scheduleStartDate.value, day) : util.date.previousDate(scheduleStartDate.value),
                    day_of_week: day,
                    stay_type: props.searchLivingPreference
                })))
            })

            return data;
        })

        generateShiftBlocks()
    }, { immediate: true, deep: true})

    const confirmedText = computed(() => {
        if (!props.caregiver.confirmed_at && !props.caregiver.confirmed_staff) {
            return 'Never Confirmed';
        }

        let text = 'Last Confirmed';
        if (props.caregiver.confirmed_at) {
            text += ' on ' + new Date(props.caregiver.confirmed_at).toLocaleDateString()
        }
        if (props.caregiver.confirmed_staff) {
            text += ' by ' + props.caregiver.confirmed_staff.first_name + ' ' + props.caregiver.confirmed_staff.last_name
        }
        return text;
    })

    const preferencesText = computed(() => {
        const fillIn = props.caregiver.availability_pref_fill_ins_flexible ? 'Flexible for Fill-Ins' : 'Not Flexible for Fill-Ins'
        const weekends = props.caregiver.availability_pref_alternate_weekends_only ? 'Alternate Weekends Only' : 'Regular Weekends'
        return fillIn + ' · ' + weekends;
    })

    const notesText = computed(() => {
        return 'Notes: ' + (props.caregiver.availability_notes ? props.caregiver.availability_notes : 'N/A');
    })

</script>

<template>

    <div class="grid grid-cols-6 grid-rows-2 px-4 gap-2">

        <div class="col-span-1 row-span-2 w-full flex flex-col gap-4">

            <label class="flex flex-col gap-1 items-center">
                <span class="w-full text-left text-xs font-bold select-none text-lifeworx-blue-800">Display Mode</span>
                <div class="w-full flex flex-1 items-center gap-2 ">
                    <span :class="[
                        'text-xs text-right font-bold select-none',
                        {
                            'text-lifeworx-green-700': !verboseMode,
                            'text-lifeworx-green-800/50': verboseMode,
                        }
                    ]"
                    >Minimal</span>
                    <div class="text-center">
                        <LwxSwitch
                            v-model="verboseMode"
                        />
                    </div>
                    <span :class="[
                            'text-xs text-left font-bold select-none',
                            {
                                'text-lifeworx-green-700': verboseMode,
                                'text-lifeworx-green-800/50': !verboseMode,
                            }
                        ]"
                    >Detailed</span>
                </div>
            </label>

            <label class="flex flex-col gap-1 items-center">
                <span class="w-full text-left text-xs font-bold select-none text-lifeworx-blue-800">Shift View</span>
                <div class="w-full flex flex-1 items-center gap-2 ">
                    <span :class="[
                        'text-xs text-right font-bold select-none',
                        {
                            'text-lifeworx-green-700': !showAssignedShifts,
                            'text-lifeworx-green-800/50': showAssignedShifts,
                        }
                    ]"
                    >Worked</span>
                    <div class="text-center">
                        <LwxSwitch
                            v-model="showAssignedShifts"
                            palette="blue"
                        />
                    </div>
                    <span :class="[
                            'text-xs text-left font-bold select-none',
                            {
                                'text-lifeworx-blue-700': showAssignedShifts,
                                'text-lifeworx-blue-800/50': !showAssignedShifts,
                            }
                        ]"
                    >Assigned</span>
                </div>
            </label>

        </div>

        <div class="col-span-2 px-12 row-span-2 gap-3 flex flex-col justify-center content-center">

            <div ref="liveInSwatch" :class="[
                'flex items-center gap-1.5 cursor-default select-none rounded-md px-1.5 py-0.5 -mx-1.5 transition-colors duration-150',
                { 'opacity-25 pointer-events-none': !liveInBlocks || !liveInBlocks.length },
                { 'bg-lifeworx-purple-600/10': userFocusedOn === 'live-in' && verboseMode }
            ]">
                <span class="inline-block size-6 rounded-md border border-lifeworx-purple-600 bg-lifeworx-purple-150 border-dashed"></span>
                <span class="text-xs font-semibold text-lifeworx-purple-600">Live-In Availability</span>
            </div>
            <div ref="liveOutSwatch" :class="[
                'flex items-center gap-1.5 cursor-default select-none rounded-md px-1.5 py-0.5 -mx-1.5 transition-colors duration-150',
                { 'opacity-25 pointer-events-none': (!liveOutGroupOneBlocks || !liveOutGroupOneBlocks.length) && (!liveOutGroupTwoBlocks || !liveOutGroupTwoBlocks.length) },
                { 'bg-lifeworx-green-600/10': userFocusedOn === 'live-out' && verboseMode }
            ]">
                <span class="inline-block size-6 rounded-md border border-lifeworx-green-600 bg-lifeworx-green-150 border-dashed"></span>
                <span class="text-xs font-semibold text-lifeworx-green-600">Live-Out Availability</span>
            </div>

            <div ref="clientShiftSwatch" :class="[
                'flex items-center gap-1.5 cursor-default select-none rounded-md px-1.5 py-0.5 -mx-1.5 transition-colors duration-150',
                { 'opacity-25 pointer-events-none': showAssignedShifts ? (!clientShiftBlocks || !clientShiftBlocks.length) : (!lastPeriodShiftBlocks || !lastPeriodShiftBlocks.length) },
                { 'bg-lifeworx-blue-600/10': userFocusedOn === 'client_shift' && verboseMode }
            ]">
                <span :class="[
                    'inline-block size-6 rounded-md border p-0.5 overflow-clip',
                    showAssignedShifts
                        ? 'border-lifeworx-blue-600 bg-lifeworx-blue-150'
                        : 'border-gray-500 bg-gray-200'
                ]">
                    <span class="relative block -top-1 -left-1 h-6 w-6 rounded-[inherit] pattern-diagonal-lines pattern-bg-transparent pattern-opacity-20 pattern-size-1 pointer-events-none pattern-lifeworx-blue-900" />
                </span>
                <span :class="[
                    'text-xs font-semibold',
                    showAssignedShifts ? 'text-lifeworx-blue-600' : 'text-gray-500'
                ]">{{ showAssignedShifts ? 'Assigned' : 'Worked' }} Shifts</span>
            </div>

        </div>

        <div class="col-start-4 col-span-3 row-start-1 row-span-2 flex flex-col gap-4">
            <span class="text-xs text-lifeworx-blue-800 font-bold">{{ preferencesText }}</span>

            <span class="text-xs text-lifeworx-blue-800 font-semibold mb-1">{{ notesText }}</span>
        </div>

    </div>
    <div>
        <LatteSchedule :start_date="scheduleStartDate">
            <ol
                class="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-6 overflow-hidden"
                style="grid-template-rows: 0.4rem repeat(288, minmax(0, 1fr)) auto"
            >

                <!-- Live In Availability -->
                <LiveInViewBlock
                    v-for="block in liveInBlocks"
                    :schedule_start_date="scheduleStartDate"
                    :block="block"
                    :displayMode="searchDisplayMode"
                    :showLabel="verboseMode"
                />

                <!-- Live Out Availability -->
                <LiveOutViewBlock
                    v-for="block in liveOutGroupOneBlocks"
                    :schedule_start_date="scheduleStartDate"
                    :block="block"
                    :displayMode="searchDisplayMode"
                    :showLabel="verboseMode"
                />
                <LiveOutViewBlock
                    v-for="block in liveOutGroupTwoBlocks"
                    :schedule_start_date="scheduleStartDate"
                    :block="block"
                    overlap_class="ml-8"
                    :displayMode="searchDisplayMode"
                    :showLabel="verboseMode"
                />

                <!-- Assigned Client Blocks -->
                <ClientCaregiverViewBlock
                    v-if="showAssignedShifts"
                    v-for="block in clientShiftBlocks"
                    :schedule_start_date="scheduleStartDate"
                    :block="block"
                    :displayMode="searchDisplayMode"
                    :showLabel="verboseMode"
                />

                <!-- Past Period Worked Blocks -->
                <ClientCaregiverViewBlock
                    v-if="!showAssignedShifts"
                    v-for="block in lastPeriodShiftBlocks"
                    :schedule_start_date="pastScheduleStartDate"
                    :block="block"
                    :displayMode="searchDisplayMode"
                    :showLabel="verboseMode"
                />

                <!-- Searched Blocks -->
                <SearchViewBlock
                    v-for="block in searchBlocks"
                    :schedule_start_date="scheduleStartDate"
                    :block="block"
                    :displayMode="searchDisplayMode"
                    :care_schedule_search_match="
                        block.continuation
                            ? props.caregiver.care_schedule_search[util.date.previousDayOfWeek(block.day_of_week)]
                            : props.caregiver.care_schedule_search[block.day_of_week]"
                />
            </ol>
        </LatteSchedule>
    </div>
    <div class="flex justify-center mt-2">
        <span class="text-xs text-lifeworx-blue-800 font-semibold">{{ confirmedText }}</span>
    </div>

</template>
